import ProductDrawerAction from "@/components/drawers/ProductDrawer/ProductDrawerAction";
import CrossReference from "@/components/links/CrossReference/CrossReference";
import List, { ListDataItem } from "@/components/visualizations/List/List";
import { useDateRange } from "@/pages/dashboard/hooks/useDateRange";
import {
  ProductBy,
  useProductImpactTopContributors,
  useProductImpactTotals,
} from "@/pages/dashboard/hooks/useProductImpact";
import { useEffect, useState } from "react";
import { ImpactMetric } from "../../../utils/types";

const step = 5;

export default function ProductImpactTopContributorsPerProduct({
  metric,
  by,
}: {
  metric: ImpactMetric;
  by: ProductBy;
}) {
  const [limit, setLimit] = useState(step);
  const { startDate, endDate } = useDateRange();

  useEffect(() => {
    setLimit(step);
  }, [startDate, endDate, by]);

  const { data, isLoading, isFetching } = useProductImpactTopContributors({
    page: 1,
    pageSize: limit + step,
    by,
    metric: metric.key,
  });

  const { data: total } = useProductImpactTotals({ by, metrics: [metric.key] });

  const numMore = Math.min((data?.top_contributors.length || 0) - limit);

  const listData = data
    ? data?.top_contributors.slice(0, limit).map((row) => {
        return {
          label: row.label,
          id: row.id,
          value: row.impact[metric.key],
        };
      })
    : [];

  interface TopContributorsList extends ListDataItem {
    id: number;
  }

  return (
    <List<TopContributorsList>
      loading={isLoading || isFetching}
      data={{
        list: listData,
        total: total?.total_impact[metric.key],
      }}
      loadMore={{
        show: numMore > 0,
        numberOfMore: numMore,
        fn: () => setLimit(limit + step),
      }}
      customRenderer={(item) => (
        <CrossReference to={`/products/${item.id}`}>
          <ProductDrawerAction
            productId={item.id}
            metric={metric}
            by={by}
          >
            {item.label}
          </ProductDrawerAction>
        </CrossReference>
      )}
      format={metric.formatter}
    />
  );
}
